import {
  Box,
  Flex,
  mdBumps,
  PrimaryButton,
  smBumps,
  TextInput,
  Typo,
} from '@wrisk/ui-components'
import { TFunction } from 'i18next'
import React, {
  ChangeEvent,
  FunctionComponent,
  HTMLInputTypeAttribute,
  useCallback,
  useState,
} from 'react'

import { with404ErrorHandling } from '../../../../clients/api'
import { GetDataEntry } from '../../../../domain'
import { useApiErrorHandlingAsyncCallback } from '../../../../hooks/auth'
import { TKeyBuilder } from '../../../../infrastructure/internationalisation'
import { usePrincipal } from '../../../authentication'

interface DataEntrySearchViewProps {
  name: string
  value?: GetDataEntry

  tKey: TKeyBuilder
  t: TFunction

  onChange: (entry: GetDataEntry) => void
  tableCode: string

  characterLimit?: number
  type?: HTMLInputTypeAttribute
  regExp?: string
}

export const DataEntrySearchView: FunctionComponent<DataEntrySearchViewProps> = ({
  tableCode,
  tKey,
  t,
  name,
  value,
  onChange,
  characterLimit,
  regExp,
  type,
}) => {
  const { apiClient } = usePrincipal()

  const [textValue, setTextValue] = useState(value?.code ?? '')
  const [valid, setValid] = useState(Boolean(value))

  const onSubmit = useApiErrorHandlingAsyncCallback(async () => {
    const entry = await with404ErrorHandling(() =>
      apiClient.getDataEntry(tableCode, textValue),
    )
    if (entry) {
      onChange(entry)
    }
  })

  const onInputChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const value = target.value.toUpperCase()

      onSubmit.reset()
      setValid(!regExp || new RegExp(regExp).test(value))

      if (!characterLimit || value.length <= characterLimit) {
        setTextValue(value)
      }
    },
    [characterLimit, onSubmit, regExp],
  )

  const errorText = Boolean(onSubmit.status === 'success' && !onSubmit.result) && (
    <Box mt={smBumps}>
      <Typo color='textCritical'>{t(tKey(name, 'validation.noResults'))}</Typo>
    </Box>
  )

  const onInputKeyUp = useCallback(
    async (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        await onSubmit.execute()
      }
    },
    [onSubmit],
  )

  return (
    <Box>
      <Flex variant='raised' p={mdBumps} flexWrap='wrap' gap={mdBumps}>
        <TextInput
          flexGrow={1}
          type={type ?? 'text'}
          variant='ghost'
          value={textValue}
          onChange={onInputChange}
          placeholder={t(tKey(name, 'placeholder'), { defaultValue: null })}
          onKeyUp={onInputKeyUp}
          maxLength={characterLimit}
          style={{ paddingTop: 0, paddingBottom: 0 }}
        />
        <PrimaryButton
          layout='small'
          loading={onSubmit.loading}
          onClick={onSubmit.execute}
          disabled={!valid}
          data-testid='search'
          whiteSpace='nowrap'
        >
          {t(tKey(name, 'actions.check'))}
        </PrimaryButton>
      </Flex>
      {errorText}
    </Box>
  )
}
